import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { VpsAgentPicker, VpsPageHeader, useVpsAgents } from './vpsShared';

const HOURS = [24, 48, 72, 96, 120, 144, 168];

function defaultCheckpoints() {
  return HOURS.map((hours) => ({ hours, enabled: true, batchSize: 20 }));
}

function armLabel(arm) {
  if (arm === 'dob') return 'Date of birth';
  if (arm === 'phone') return 'Phone';
  return 'No date of birth';
}

function settingsBody(checkpoints, maxConcurrent, browseMin, browseMax, noDobPercent) {
  return {
    maxConcurrent: Number(maxConcurrent) || 100,
    browseMinMs: Number(browseMin) * 60_000,
    browseMaxMs: Number(browseMax) * 60_000,
    noDobShare: Number(noDobPercent) / 100,
    checkpoints,
  };
}
function notifLabel(status) {
  if (status === 'received') return 'Received';
  if (status === 'silent') return 'Silent';
  if (status === 'pending') return 'Waiting';
  if (status === 'skipped') return 'Not sent';
  return '—';
}

function dobLabel(row) {
  if (row.dobAlreadySet) return 'Set already';
  if (row.dobAddedAt) return 'Added';
  if (row.dobError) return row.dobError;
  if (row.identityCheckError) return 'Check failed';
  return '—';
}

export default function VpsWarmup() {
  const { user } = useAuth();
  const { agents, selectedId, selectAgent, reload } = useVpsAgents();
  const agent = agents.find((row) => String(row._id) === String(selectedId)) || null;

  const [checkpoints, setCheckpoints] = useState(defaultCheckpoints);
  const [maxConcurrent, setMaxConcurrent] = useState(100);
  const [browseMin, setBrowseMin] = useState(12);
  const [browseMax, setBrowseMax] = useState(15);
  const [noDobPercent, setNoDobPercent] = useState(50);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountFilter, setAccountFilter] = useState('all');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  const hydratedSession = React.useRef('');

  const loadBoard = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/va/ai-agents/${selectedId}/warmup/board`);
      if (!res.data?.success) return;
      setBoard(res.data);
      const session = res.data.session;
      const sessionKey = session?._id ? String(session._id) : '';
      if (!sessionKey || hydratedSession.current === sessionKey) return;
      hydratedSession.current = sessionKey;
      const cfg = session.config || {};
      if (Array.isArray(cfg.checkpoints) && cfg.checkpoints.length) {
        setCheckpoints(HOURS.map((hours) => {
          const row = cfg.checkpoints.find((item) => Number(item.hours) === hours);
          return {
            hours,
            enabled: row ? row.enabled !== false : false,
            batchSize: row ? Number(row.batchSize) || 0 : 20,
          };
        }));
      }
      if (session.status === 'running' && cfg.maxConcurrent) {
        setMaxConcurrent(Number(cfg.maxConcurrent) || 100);
      } else {
        setMaxConcurrent(100);
      }
      if (cfg.browseMinMs) setBrowseMin(Math.round(cfg.browseMinMs / 60_000));
      if (cfg.browseMaxMs) setBrowseMax(Math.round(cfg.browseMaxMs / 60_000));
      if (cfg.noDobShare != null) setNoDobPercent(Math.round(Number(cfg.noDobShare) * 100));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load warmup');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    hydratedSession.current = '';
    loadBoard();
    const timer = setInterval(loadBoard, 5_000);
    return () => clearInterval(timer);
  }, [loadBoard]);

  const prepared = useMemo(
    () => (board?.accounts || []).filter((row) => row.state === 'prepared').length,
    [board]
  );

  const updateCheckpoint = (hours, patch) => {
    setCheckpoints((rows) => rows.map((row) => (
      row.hours === hours ? { ...row, ...patch } : row
    )));
  };

  const start = async () => {
    if (!selectedId) return;
    if (agent && agent.automationType && agent.automationType !== 'warmup') {
      const ok = window.confirm(
        'This VPS is not a warmup agent. Starting will switch it to warmup and stop follow dispatch on it. Continue?'
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/warmup/start`, {
        ...settingsBody(checkpoints, maxConcurrent, browseMin, browseMax, noDobPercent),
        phase2Days: 0,
      });
      const assigned = res.data?.profileAssign?.assigned;
      const assignError = res.data?.profileAssign?.error;
      toast.success(
        assigned
          ? `Checking accounts and creating Orbex profiles first. Assigned ${assigned} user profile${assigned === 1 ? '' : 's'}.`
          : 'Checking accounts and creating Orbex profiles. Browse starts after that.'
      );
      if (assignError) toast.error(assignError);
      hydratedSession.current = '';
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start warmup');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.post(`/admin/va/ai-agents/${selectedId}/warmup/stop`, {});
      toast.success('Warmup stopped');
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop warmup');
    } finally {
      setBusy(false);
    }
  };

  const applyLimits = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/va/ai-agents/${selectedId}/warmup/settings`,
        settingsBody(checkpoints, maxConcurrent, browseMin, browseMax, noDobPercent)
      );
      toast.success('Limits updated. The next desktop pull uses them.');
      hydratedSession.current = '';
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update limits');
    } finally {
      setBusy(false);
    }
  };

  const checkAll = async () => {
    if (!selectedId) return;
    const ok = window.confirm(
      'Test every account that has not followed yet today? Each one follows another account in this warmup, then we check whether the notification arrived. Browsers stay capped at the concurrency you set.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${selectedId}/warmup/probe-all`, {});
      toast.success(`Queued ${res.data?.queued ?? 0} accounts to test today.`);
      await loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue the full check');
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== 'admin') {
    return <div className="text-sm text-gray-500">Admin access required.</div>;
  }

  const running = board?.session?.status === 'running';
  const preparing = board?.session?.status === 'preparing';
  const following = board?.session?.status === 'following';
  const checking = board?.session?.status === 'checking';
  const live = running || preparing || following || checking;
  const phaseLabel = preparing
    ? 'Checking date of birth and phone'
    : following
      ? 'Sending one follow each'
      : checking
        ? 'Checking follow notifications'
        : running
          ? 'Running'
          : 'Stopped';
  const accounts = board?.accounts || [];
  const preparedRows = accounts.filter((row) => row.state === 'prepared');
  const visibleAccounts = accounts.filter((row) => {
    if (accountFilter === 'prepared') return row.state === 'prepared';
    if (accountFilter === 'warming') return row.state !== 'prepared';
    return true;
  }).slice().sort((a, b) => {
    if (a.state === 'prepared' && b.state !== 'prepared') return -1;
    if (b.state === 'prepared' && a.state !== 'prepared') return 1;
    return String(a.username || '').localeCompare(String(b.username || ''));
  });
  const pickedRows = accounts.filter((row) => picked[row.accountId]);

  function togglePicked(id) {
    setPicked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function selectPrepared() {
    const next = {};
    for (const row of preparedRows) next[row.accountId] = true;
    setPicked(next);
  }

  async function copyPicked() {
    const names = pickedRows.map((row) => row.username).filter(Boolean);
    if (!names.length) return;
    try {
      await navigator.clipboard.writeText(names.join('\n'));
      toast.success(`Copied ${names.length} username${names.length === 1 ? '' : 's'}`);
    } catch (_) {
      toast.error('Could not copy usernames');
    }
  }

  function accountStateLabel(row) {
    if (row.accountStatus && row.accountStatus !== 'active') {
      if (row.accountStatus === 'auth_expired') return 'Logged out';
      if (row.accountStatus === 'captcha') return 'Captcha';
      if (row.accountStatus === 'suspended') return 'Suspended';
      return row.accountStatus;
    }
    if (row.state === 'prepared') return 'Prepared';
    return row.status || 'browsing';
  }
  const summary = board?.summary || [];
  const stage = board?.stage || null;

  return (
    <div className="space-y-6">
      <VpsPageHeader
        title="VPS Warmup"
        subtitle="Daily browse in Orbex on this VPS, then a small follow batch at each checkpoint. A follow notification means the account is prepared."
        actions={(
          <VpsAgentPicker
            agents={agents}
            selectedId={selectedId}
            onSelect={selectAgent}
            onChanged={async (id) => {
              await reload({ force: true, silent: true });
              if (id) selectAgent(id);
            }}
          />
        )}
      />

      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Browsers at once</span>
            <input
              type="number"
              min={1}
              max={200}
              className="form-input w-28"
              value={maxConcurrent}
              onChange={(e) => setMaxConcurrent(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Browse minutes</span>
            <span className="inline-flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={60}
                className="form-input w-20"
                value={browseMin}
                onChange={(e) => setBrowseMin(e.target.value)}
              />
              <span className="text-gray-400">to</span>
              <input
                type="number"
                min={1}
                max={60}
                className="form-input w-20"
                value={browseMax}
                onChange={(e) => setBrowseMax(e.target.value)}
              />
            </span>
          </label>
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">No date of birth</span>
            <span className="inline-flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                className="form-input w-20"
                value={noDobPercent}
                onChange={(e) => setNoDobPercent(e.target.value)}
              />
              <span className="text-gray-400">%</span>
            </span>
          </label>
          <button type="button" className="btn-primary !py-2 !px-4 text-sm" disabled={busy || !selectedId || preparing} onClick={start}>
            {preparing ? 'Preparing…' : (running ? 'Restart warmup' : 'Start warmup')}
          </button>
          <button type="button" className="btn-secondary !py-2 !px-4 text-sm" disabled={busy || !selectedId} onClick={applyLimits}>
            Apply limits
          </button>
          <button type="button" className="btn-secondary !py-2 !px-4 text-sm" disabled={busy || !running} onClick={checkAll}>
            Check all accounts today
          </button>
          <button type="button" className="btn-secondary !py-2 !px-4 text-sm" disabled={busy || !live} onClick={stop}>
            Stop
          </button>
          <span className="text-sm text-gray-500">
            {stage ? stage.label : (preparing ? 'Checking accounts, then creating Orbex profiles' : phaseLabel)}
            {accounts.length ? ` · ${accounts.length} accounts · ${prepared} prepared` : ''}
            {board?.session?.config?.probeAllDate ? ' · checking all today' : ''}
            {board?.session?.config?.prepareError ? ` · ${board.session.config.prepareError}` : ''}
          </span>
        </div>

        {stage && stage.stage !== 'idle' ? (
          <div>
            <div className="mb-1 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>{stage.label}</span>
              <span className="tabular-nums">
                {stage.percent}%
                {stage.total ? ` · ${stage.done} / ${stage.total}` : ''}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full ${stage.stopped ? 'bg-gray-400' : 'bg-blue-500'}`}
                style={{ width: `${stage.percent}%` }}
              />
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          {[
            ['Ready today', board?.today?.ready ?? 0],
            ['Tested today', board?.today?.tested ?? 0],
            ['Notification received', board?.today?.received ?? 0],
            ['Silent today', board?.today?.silent ?? 0],
            ['Waiting today', board?.today?.pending ?? 0],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
              <div className="text-gray-500">{label}</div>
              <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{value}</div>
            </div>
          ))}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-3 font-medium">Checkpoint</th>
              <th className="py-1 pr-3 font-medium">On</th>
              <th className="py-1 font-medium">Follows that window</th>
            </tr>
          </thead>
          <tbody>
            {checkpoints.map((row) => (
              <tr key={row.hours} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-1.5 pr-3">{row.hours}h · {row.hours / 24} day{row.hours === 24 ? '' : 's'}</td>
                <td className="py-1.5 pr-3">
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={(e) => updateCheckpoint(row.hours, { enabled: e.target.checked })}
                  />
                </td>
                <td className="py-1.5">
                  <input
                    type="number"
                    min={0}
                    max={500}
                    className="form-input w-24"
                    value={row.batchSize}
                    onChange={(e) => updateCheckpoint(row.hours, { batchSize: Number(e.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500">
          The follow batch is split by the date-of-birth share. A sender never follows the same account twice.
          The user profile already on the account is left in place and sent to the desktop agent with the job.
          Phone stays empty until an SMS service is connected.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">When notifications arrived</h2>
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 font-medium">Checkpoint</th>
                <th className="px-3 py-2 font-medium">Arm</th>
                <th className="px-3 py-2 font-medium text-right">Received</th>
                <th className="px-3 py-2 font-medium text-right">Silent</th>
                <th className="px-3 py-2 font-medium text-right">Waiting</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={`${row.checkpointHours}-${row.arm}`} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="px-3 py-2">{row.checkpointHours ? `${row.checkpointHours}h` : 'Check all'}</td>
                  <td className="px-3 py-2">{armLabel(row.arm)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.received}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.silent}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.pending}</td>
                </tr>
              ))}
              {!summary.length && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    {loading ? 'Loading…' : 'No follow probes yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mr-2">Accounts</h2>
          {[
            ['all', `All ${accounts.length}`],
            ['prepared', `Prepared ${preparedRows.length}`],
            ['warming', `Still warming ${accounts.length - preparedRows.length}`],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`text-xs px-2 py-1 rounded-full border ${accountFilter === id ? 'border-blue-500 text-blue-600' : 'border-gray-300 text-gray-500'}`}
              onClick={() => setAccountFilter(id)}
            >
              {label}
            </button>
          ))}
          <button type="button" className="btn-secondary !py-1 !px-3 text-xs" disabled={!preparedRows.length} onClick={selectPrepared}>
            Select prepared
          </button>
          <button type="button" className="btn-secondary !py-1 !px-3 text-xs" disabled={!pickedRows.length} onClick={copyPicked}>
            Copy {pickedRows.length || ''} selected
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Prepared accounts have sent a follow notification. They leave this warmup. Select them here and copy the usernames when you move them to a follow VPS.
        </p>
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 font-medium">
                  <input
                    type="checkbox"
                    checked={visibleAccounts.length > 0 && visibleAccounts.every((row) => picked[row.accountId])}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setPicked((prev) => {
                        const next = { ...prev };
                        for (const row of visibleAccounts) next[row.accountId] = on;
                        return next;
                      });
                    }}
                  />
                </th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">User profile</th>
                <th className="px-3 py-2 font-medium">Arm</th>
                <th className="px-3 py-2 font-medium text-right">Warmup days</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Last probe</th>
                <th className="px-3 py-2 font-medium">Notification</th>
                <th className="px-3 py-2 font-medium">Date of birth</th>
                <th className="px-3 py-2 font-medium">Phone</th>
              </tr>
            </thead>
            <tbody>
              {visibleAccounts.map((row) => (
                <tr
                  key={row.accountId}
                  className={`border-t border-gray-100 dark:border-gray-800 ${row.state === 'prepared' ? 'bg-emerald-50/70 dark:bg-emerald-950/30' : ''}`}
                >
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={Boolean(picked[row.accountId])} onChange={() => togglePicked(row.accountId)} />
                  </td>
                  <td className="px-3 py-2">@{row.username || '—'}</td>
                  <td className="px-3 py-2">{row.userProfileName || '—'}</td>
                  <td className="px-3 py-2">{armLabel(row.arm)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.warmupDays || 0}</td>
                  <td className="px-3 py-2 capitalize">{accountStateLabel(row)}</td>
                  <td className="px-3 py-2">
                    {row.lastProbe
                      ? `${row.lastProbe.checkpointHours ? `${row.lastProbe.checkpointHours}h` : 'Start'} → @${row.lastProbe.targetUsername || '—'}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">{notifLabel(row.lastProbe?.notifStatus)}</td>
                  <td className="px-3 py-2">{dobLabel(row)}</td>
                  <td className="px-3 py-2">{row.phoneAlreadySet ? 'Set already' : '—'}</td>
                </tr>
              ))}
              {!visibleAccounts.length && (
                <tr>
                  <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                    {loading ? 'Loading…' : 'No accounts in this warmup yet. Attach them on VPS Accounts, then start.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
